import {
  Component,
  inject,
  signal,
  ViewChild,
  WritableSignal,
} from "@angular/core";
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { TranslocoDirective } from "@jsverse/transloco";
import { AutoComplete, AutoCompleteModule } from "primeng/autocomplete";
import { ButtonModule } from "primeng/button";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { IconFieldModule } from "primeng/iconfield";
import { InputIconModule } from "primeng/inputicon";
import { InputTextModule } from "primeng/inputtext";
import { MultiSelectModule } from "primeng/multiselect";
import { SelectModule } from "primeng/select";
import { TeamMemberRoleEnum } from "../../enum/team-member-role.enum";
import { FilterObject } from "../../interfaces/filter-object.interface";
import { Pagination } from "../../interfaces/pagination.interface";
import { Project } from "../../interfaces/project.interface";
import { TeamMember } from "../../interfaces/team-member.interface";
import {
  AddressAutocompleteService,
  GeoApifyAddress,
} from "../../services/address-autocomplete.service";
import { TeamManagementService } from "../../services/team-management.service";

@Component({
  selector: "app-add-new-project-dialog",
  imports: [
    TranslocoDirective,
    ButtonModule,
    MultiSelectModule,
    FormsModule,
    ReactiveFormsModule,
    InputTextModule,
    AutoCompleteModule,
    IconFieldModule,
    InputIconModule,
    SelectModule,
  ],
  templateUrl: "./add-new-project-dialog.component.html",
  styleUrl: "./add-new-project-dialog.component.scss",
})
export class AddNewProjectDialogComponent {
  filteredAddresses: WritableSignal<GeoApifyAddress[]> = signal([]);
  error: string = "";
  projectManagers: WritableSignal<TeamMember[]> = signal([]);
  isEdit: WritableSignal<boolean> = signal(false);
  @ViewChild(AutoComplete) addressAuto?: AutoComplete;
  private config = inject(DynamicDialogConfig<{ project?: Project }>);

  form: FormGroup = new FormGroup({
    title: new FormControl(null, [Validators.required]),
    address: new FormControl(null, [Validators.required]),
    projectManager: new FormControl(null, [Validators.required]),
    quantitiesTakeOffSheetUrl: new FormControl(null, [
      Validators.required,
      Validators.pattern(/https?:\/\/[^\s$.?#].[^\s]*$/),
    ]),
  });

  constructor(
    public ref: DynamicDialogRef,
    private addressAutocompleteService: AddressAutocompleteService,
    private teamManagementService: TeamManagementService,
  ) {}

  ngOnInit(): void {
    this.addressAutocompleteService.initializeSearch().subscribe({
      next: (addresses) => {
        this.filteredAddresses.set(addresses);
      },
      error: (error) => {
        console.error("Search error:", error);
        this.error = "Failed to search addresses. Please try again.";
        this.filteredAddresses.set([]);
      },
    });
    const pag: Pagination = {
      page: 0,
      size: 1000,
      sort: "name,asc",
    };
    const filter: FilterObject = {
      roles: [TeamMemberRoleEnum.ADMIN, TeamMemberRoleEnum.PROJECT_MANAGER],
    };
    this.teamManagementService.getTeamMembers(pag, filter).subscribe((res) => {
      this.projectManagers.set(res.content);
      const project = this.config.data?.project;
      if (project) {
        this.isEdit.set(true);
        this.preFillForm(project);
      }
    });
  }

  private preFillForm(project: Project): void {
    const address = project.address;
    const formatted = [
      address.street,
      address.city,
      address.zipCode,
      address.country,
    ]
      .filter(Boolean)
      .join(", ");
    const geoAddress: GeoApifyAddress = {
      place_id: "",
      formatted,
      address_line1: address.street,
      city: address.city,
      postcode: address.zipCode,
      country: address.country,
      lat: address.latitude,
      lon: address.longitude,
    };
    const projectManager = this.projectManagers().find(
      (m) => m.id === project.projectManagerId,
    );
    this.form.patchValue({
      title: project.title,
      address: geoAddress,
      projectManager: projectManager ?? null,
      quantitiesTakeOffSheetUrl: project.quantitiesTakeOffSheetUrl,
    });
    // The autocomplete is intentionally NOT bound via formControlName (typing
    // must not write into the control). Drive its display directly so the
    // current address is shown for editing.
    this.addressAuto?.writeValue(geoAddress);
  }

  filterAddresses(event: any): void {
    const query = event.query;
    if (query && query.length >= 3) {
      this.error = "";
      this.addressAutocompleteService.searchAddress(query);
    } else {
      this.filteredAddresses.set([]);
    }
  }

  onAddressSelect(address: GeoApifyAddress): void {
    this.form.get("address")?.patchValue(address);
    this.error = "";
  }

  onAddressClear(): void {
    this.form.get("address")?.patchValue(null);
    this.filteredAddresses.set([]);
    this.error = "";
  }

  onSaveClick(): void {
    const result = this.form.getRawValue();
    this.ref.close(result);
  }
}

export interface NewProjectForm {
  title: string;
  address: GeoApifyAddress;
  projectManager: TeamMember;
  quantitiesTakeOffSheetUrl: string;
}
